import { supabase } from "@/lib/supabase";
import { toast } from "sonner";
import { logSecurityEvent } from "./securityUtils";
import { getDeviceFingerprint, getDeviceInfo, getBotProbability } from "./deviceInfo";

/**
 * Check rate limit before performing sensitive operations
 * @param endpoint The logical name of the endpoint being accessed
 * @returns Whether the request is allowed
 */
export async function checkRateLimit(endpoint: string): Promise<boolean> {
  try {
    // Get IP address via a service (we can't get it directly in the browser)
    let ip = "unknown";
    try {
      const ipResponse = await fetch('https://api.ipify.org?format=json');
      if (ipResponse.ok) {
        const data = await ipResponse.json();
        ip = data.ip || "unknown";
      }
    } catch (error) {
      console.error("Failed to get IP address:", error);
    }

    const fingerprint = getDeviceFingerprint();
    
    // Call our rate limiting edge function
    const { data, error } = await supabase.functions.invoke('rate-limit-check', {
      body: { 
        ip, 
        endpoint,
        fingerprint 
      },
    });

    if (error) {
      console.error("Rate limit check error:", error);
      return true; // Fail open - allow the request if check fails
    }

    if (data && !data.allowed) {
      // Request is rate limited
      toast.error(`Too many requests. Please try again in ${Math.ceil(data.remainingTime / 60)} minutes`);
      return false;
    }

    return true; // Request is allowed
  } catch (error) {
    console.error("Error checking rate limit:", error);
    return true; // Fail open - allow the request if check fails
  }
}

/**
 * Records user activity for security monitoring purposes
 * @param userId The ID of the current user
 * @param activityType The type of activity being performed
 * @param details Additional details about the activity
 */
export async function recordUserActivity(
  userId: string | undefined,
  activityType: string,
  details: Record<string, any> = {}
): Promise<void> {
  if (!userId) return;
  
  try {
    // Check if this is a suspected bot
    const botProbability = getBotProbability();
    
    // Add device fingerprint and bot probability to details
    const enhancedDetails = {
      ...details,
      fingerprint: getDeviceFingerprint(),
      botProbability,
      deviceInfo: getDeviceInfo(),
    };

    // Record the activity in the database
    await supabase.from("user_activities").insert({
      user_id: userId,
      activity_type: activityType,
      details: enhancedDetails,
      created_at: new Date().toISOString(),
    });

    // If this looks like a bot with high probability, log a security event
    if (botProbability > 0.7) {
      await logSecurityEvent(
        userId,
        'suspected_bot_activity',
        `Suspected bot activity detected during ${activityType}`,
        { 
          botProbability,
          activityType,
          ...enhancedDetails
        }
      );
    }
  } catch (error) {
    console.error("Failed to record user activity:", error);
  }
}

/**
 * Terminates all user sessions except the current one
 * @param userId The ID of the current user
 * @param currentSessionId The ID of the current session to keep
 */
export async function terminateOtherSessions(
  userId: string,
  currentSessionId: string
): Promise<void> {
  try {
    // Log the security event
    await logSecurityEvent(
      userId, 
      "session_termination", 
      "Terminating other sessions due to security concerns", 
      { 
        currentSessionId,
        fingerprint: getDeviceFingerprint(),
        deviceInfo: getDeviceInfo()
      }
    );
    
    // Call Supabase function to terminate other sessions
    await supabase.functions.invoke('terminate-other-sessions', {
      body: { currentSessionId }
    });
    
    toast.success("All other sessions have been terminated for security reasons.");
  } catch (error) {
    console.error("Failed to terminate other sessions:", error);
    toast.error("Failed to secure your account. Please contact support.");
  }
}

/**
 * Checks for suspicious activity related to a user account
 * @param userId The user ID to check
 * @returns Whether suspicious activity was detected
 */
export async function checkSuspiciousActivity(userId: string): Promise<boolean> {
  try {
    // Get recent security logs for this user
    const { data: logs, error } = await supabase
      .from('security_logs')
      .select('*')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .limit(20);
      
    if (error) {
      console.error("Error checking security logs:", error);
      return false;
    }
    
    // No logs means no activity to analyze
    if (!logs || logs.length === 0) return false;
    
    // Check for multiple login locations in short time
    const loginAttempts = logs.filter(log => 
      log.event_type === 'login_attempt' || 
      log.event_type === 'session_created'
    );
    
    if (loginAttempts.length >= 3) {
      // Check for different IP addresses or devices
      const uniqueIps = new Set(loginAttempts.map(log => log.ip_address)).size;
      const uniqueDevices = new Set(loginAttempts.map(log => log.browser_fingerprint)).size;
      
      if (uniqueIps >= 3 || uniqueDevices >= 3) {
        return true; // Suspicious: Multiple locations/devices
      }
    }
    
    // Check for failed login attempts
    const failedLogins = logs.filter(log => log.event_type === 'login_failed');
    if (failedLogins.length >= 5) {
      return true; // Suspicious: Multiple failed login attempts
    }
    
    // Check for password reset attempts
    const passwordResets = logs.filter(log => log.event_type === 'password_reset_requested');
    if (passwordResets.length >= 2) {
      return true; // Suspicious: Multiple password reset attempts
    }
    
    // Check for bot probability
    const botActivities = logs.filter(log => 
      log.event_type === 'suspected_bot_activity' ||
      (log.metadata && log.metadata.botProbability && log.metadata.botProbability > 0.7)
    );
    
    if (botActivities.length >= 1) {
      return true; // Suspicious: Bot activity detected
    }
    
    return false; // No suspicious activity detected
  } catch (error) {
    console.error("Error in checkSuspiciousActivity:", error);
    return false; // Fail safe - don't block legitimate users
  }
}

/**
 * Checks if the user's password needs to be changed due to age policy
 * @param userId The user ID to check
 * @param lastPasswordChange Timestamp when password was last changed
 * @returns Whether the password needs to be changed
 */
export async function checkPasswordAgePolicies(userId: string, lastPasswordChange: Date): Promise<boolean> {
  try {
    // Get the password policy
    const { data: policy } = await supabase
      .from('password_policies')
      .select('max_age_days')
      .single();
    
    if (!policy || !policy.max_age_days) {
      return false; // No policy or no max age set
    }
    
    const maxAgeMs = policy.max_age_days * 24 * 60 * 60 * 1000;
    const passwordAgeMs = Date.now() - lastPasswordChange.getTime();
    
    if (passwordAgeMs > maxAgeMs) {
      // Password is too old
      await logSecurityEvent(
        userId,
        'password_expired',
        'Password exceeded maximum age policy',
        { 
          passwordAgeInDays: Math.floor(passwordAgeMs / (24 * 60 * 60 * 1000)),
          maxAgeInDays: policy.max_age_days
        }
      );
      
      return true; // Password needs to be changed
    }
    
    return false; // Password is still within age policy
  } catch (error) {
    console.error("Error checking password age policy:", error);
    return false; // Fail open - don't require change if check fails
  }
}
